import React, { useState } from "react";
import StaticContentPage from "./StaticContentPage";
import { trackYourOrderText } from "../data/staticPagesContent";
import { useOrdersStatusReadQuery } from "../store/api.ts";
import {
  formatPrice,
  getFirstAvailableAmount,
  getOrderItemQuantity,
  getOrderItemLineTotal,
  getOrderItemOriginalLineTotal,
  getOrderItemSavings,
  hasOrderItemDiscount,
  getOrderTotalAmount,
  getOrderSavingsAmount,
  toAmount,
} from "../utils.js";
import { getOrderBundlePricing, groupOrderItems} from "../utils/orderBundle";

const getItemQuantity = getOrderItemQuantity;

const getOriginalUnitPrice = (item) =>
  getFirstAvailableAmount(
    item?.unit_price,
    item?.discounted_unit_price,
  );

const getDiscountedUnitPrice = (item) =>
  getFirstAvailableAmount(
    item?.discounted_unit_price,
    item?.unit_price,
  );

const getItemLineTotal = (item) => getOrderItemLineTotal(item, getDiscountedUnitPrice);

const getItemOriginalLineTotal = (item) => getOrderItemOriginalLineTotal(item, getOriginalUnitPrice);

const getItemSavings = (item) =>
  getOrderItemSavings({
    item,
    getOriginalUnitPrice,
    getLineTotal: getItemLineTotal,
  });

const hasItemDiscount = (item) => hasOrderItemDiscount(item, getItemSavings);

const getOrderTotal = (order) => getOrderTotalAmount(order, getItemLineTotal);

const getOrderSavings = (order) => getOrderSavingsAmount(order, getItemSavings);

const TrackOrderItemCard = ({ item, insideBundle = false }) => {

  const quantity = getItemQuantity(item);
  const originalUnitPrice = getOriginalUnitPrice(item);
  const discountedUnitPrice = getDiscountedUnitPrice(item);
  const originalLineTotal = getItemOriginalLineTotal(item);
  const lineTotal = getItemLineTotal(item);
  const itemSavings = getItemSavings(item);
  const itemHasDiscount = hasItemDiscount(item);
  const imageUrl = item?.product_image;
  const hasImage = imageUrl && imageUrl !== "null";

  return (
    <div className={["flex flex-col gap-4 bg-white/60 p-4 sm:flex-row",
        insideBundle
          ? ""
          : "rounded-xl border border-black/10",
      ].join(" ")}
    >
      <div className="h-[112px] w-full shrink-0 overflow-hidden rounded-lg bg-[#EDE3DD] sm:h-[96px] sm:w-[96px]">
        {hasImage ? (
          <img
            src={imageUrl}
            alt={
              item?.product_name ||
              "Product"
            }
            className="h-full w-full object-cover"
          />
        ) : null}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h4 className="text-[15px] font-semibold text-[#22272A]">
            {item?.product_name || "Product"}
          </h4>

          {!insideBundle && toAmount(item?.discount_percent ) > 0 ? (
            <span className="rounded-[4px] bg-[#EAF7EE] px-2 py-0.5 text-[10px] font-semibold text-[#257A45]">
              {toAmount(item.discount_percent)} % OFF
            </span>
          ) : null}
        </div>

        <div className="mt-2 grid grid-cols-1 gap-2 text-[14px] text-[#22272A]/75 sm:grid-cols-2">
          <p>
            Size:{" "}
            {item?.size
              ? String(item.size).toUpperCase()
              : "—"}
          </p>
          <p>Quantity: {quantity || "—"}</p>
          <div className="flex items-center gap-2">
            <span>Color:</span>
            {item?.color ? (
              <>
                <span
                  className="inline-block h-3.5 w-3.5 rounded-full border border-black/15"
                  style={{
                    backgroundColor: item.color,
                  }}
                />
                <span>{item.color}</span>
              </>
            ) : (
              <span>—</span>
            )}
          </div>
          {item?.sku ? (
            <p>SKU: {item.sku}</p>
          ) : null}
        </div>
      </div>

      <div className="shrink-0 border-t border-black/10 pt-3 text-left sm:min-w-[150px] sm:border-l sm:border-t-0 sm:pl-4 sm:pt-0 sm:text-right">
        <p className="text-[11px] uppercase tracking-[0.06em] text-[#22272A]/50">
          Unit Price
        </p>

        <div className="mt-1 flex flex-wrap items-center gap-2 sm:justify-end">
          {itemHasDiscount ? (
            <span className="text-[13px] text-[#8A8A8A] line-through">
              ${formatPrice(originalUnitPrice)}
            </span>
          ) : null}

          <span className="text-[14px] font-semibold text-[#22272A]">
            ${formatPrice(discountedUnitPrice)}
          </span>
        </div>

        <p className="mt-3 text-[11px] uppercase tracking-[0.06em] text-[#22272A]/50">
          Line Total
        </p>

        {itemHasDiscount ? (
          <p className="mt-1 text-[13px] text-[#8A8A8A] line-through">
            ${formatPrice(originalLineTotal)}
          </p>
        ) : null}

        <p className="text-[16px] font-semibold text-[#22272A]">
          ${formatPrice(lineTotal)}
        </p>

        {!insideBundle && itemSavings > 0 ? (
          <p className="mt-1 text-[12px] font-medium text-[#257A45]">
            Saved ${formatPrice(itemSavings)}
          </p>
        ) : null}
      </div>
    </div>
  );
};

const TrackOrderBundleCard = ({ bundle }) => {

  const pricing = getOrderBundlePricing(bundle);
  return (
    <div className="overflow-hidden rounded-xl border border-[#D8D3CC] bg-white">
      {/* BUNDLE HEADER */}
      <div className="border-b border-[#DDD8D1] bg-[#F7F4EF] px-4 py-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <span className="inline-flex rounded-[3px] bg-black px-2 py-1 font-spaceMono text-[8px] sm:text-[12px] font-bold uppercase tracking-[0.16em] text-white">
              Bundle
            </span>

            <h3 className="mt-2 text-[16px] font-semibold text-[#22272A]">
              {bundle?.name || "Collection Bundle"}
            </h3>

            <p className="mt-1 font-spaceMono text-[9px] sm:text-[12px] uppercase tracking-[0.08em] text-[#777777]">
              {bundle?.items?.length || 0} products
              {pricing.quantity > 1
                ? ` • Bundle Qty ${pricing.quantity}`
                : ""}
            </p>
          </div>

          {pricing.discountPercent > 0 ? (
            <span className="rounded-[4px] bg-[#EAF7EE] px-2 py-1 text-[10px] sm:text-[12px] font-semibold text-[#257A45]">
              {pricing.discountPercent}% BUNDLE SAVINGS
            </span>
          ) : null}
        </div>
      </div>

      {/* PRODUCTS */}
      <div className="divide-y divide-[#E7E3DD]">
        {(bundle?.items || []).map(
          (item, index) => (
            <TrackOrderItemCard
              key={item?.item_id || `${bundle.id}-${index}`}
              item={item}
              insideBundle
            />
          ),
        )}
      </div>

      {/* BUNDLE PRICING */}
      <div className="border-t border-[#DDD8D1] bg-[#FAF9F7] px-4 py-4">
        <div className="flex items-center justify-between gap-4 text-[14px]">
          <span className="text-[#22272A]/65">
            Original Bundle Price
          </span>
          <span className="font-medium text-[#22272A]">
            ${formatPrice(pricing.originalSubtotal)}
          </span>
        </div>

        {pricing.discountAmount > 0 ? (
          <div className="mt-3 flex items-center justify-between gap-4 text-[14px]">
            <span className="text-[#257A45]">
              Bundle Discount ({pricing.discountPercent}%)
            </span>
            <span className="font-medium text-[#257A45]">
              -${formatPrice(pricing.discountAmount)}
            </span>
          </div>
        ) : null}

        <div className="mt-3 flex items-center justify-between gap-4 border-t border-[#DDD8D1] pt-3">
          <span className="font-semibold text-[#22272A]">
            Bundle Total
          </span>

          <span className="text-[18px] font-semibold text-[#22272A]">
            ${formatPrice(pricing.total)}
          </span>
        </div>
      </div>
    </div>
  );
};

const TrackYourOrder = () => {
  const [orderIdInput, setOrderIdInput] = useState("");
  const [submittedOrderId, setSubmittedOrderId] = useState("");

  const {
    currentData: orderStatus,
    isFetching,
    isError,
    error,
    refetch,
  } = useOrdersStatusReadQuery(
    {
      orderId: submittedOrderId,
    },
    {
      skip: !submittedOrderId,
    },
  );

  const trackYourOrderBodyText = trackYourOrderText
  .replace(/^\s*Track Your Order\s*/i, "")
  .trim();

  const handleSubmit = (event) => {
    event.preventDefault();

    const nextOrderId = orderIdInput.trim();

    if (!nextOrderId) return;

    if (nextOrderId === submittedOrderId) {
      refetch?.();
      return;
    }

    setSubmittedOrderId(nextOrderId);
  };

  const getErrorMessage = () => {
    if (typeof error?.data?.detail === "string") return error.data.detail;
    if (typeof error?.data?.message === "string") return error.data.message;
    if (typeof error?.error === "string") return error.error;

    if (error?.status === 404) {
      return "No order found with this order ID.";
    }

    if (error?.status === 401 || error?.status === 403) {
      return "Unable to access this order status right now.";
    }

    return "Unable to fetch order status. Please try again.";
  };

  const formatDate = (value) => {
    if (!value) return "—";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleString();
  };

  const orderTotal = getOrderTotal(orderStatus);
  const orderSavings = getOrderSavings(orderStatus);
  const originalOrderTotal = orderTotal + orderSavings;
  const hasOrderDiscount = orderSavings > 0;
  const orderEntries = groupOrderItems(orderStatus?.items || []);

  return (
    <StaticContentPage
      title="Track Your Order"
      seoTitle="Track Your Order | Eveness"
      seoDescription="Track your Eveness order status using your tracking ID and view order progress, payment status, shipping details, and purchased items."
      canonicalPath="/track-your-order"
    >
      <div className="max-w-[860px] whitespace-pre-line font-inter text-[14px] leading-[170%] text-[#3F3F3F] sm:text-[15px]">
        {trackYourOrderBodyText}
      </div>

      <form
        onSubmit={handleSubmit}
        className="mt-10 max-w-[720px] rounded-2xl border border-black/10 bg-white/40 p-5 sm:p-6"
      >
        <label
          htmlFor="orderId"
          className="mb-2 block text-sm font-semibold text-[#22272A]"
        >
          Tracking ID
        </label>

        <div className="flex flex-col gap-3 sm:flex-row">
          <input
            id="orderId"
            type="text"
            value={orderIdInput}
            onChange={(event) => setOrderIdInput(event.target.value)}
            placeholder="Enter your order ID"
            className="min-w-0 flex-1 rounded-xl border border-black/10 bg-white/70 px-4 py-3 outline-none transition-colors focus:border-[#22272A]"
            required
          />

          <button
            type="submit"
            disabled={isFetching || !orderIdInput.trim()}
            className="rounded-full bg-[#22272A] px-8 py-3 text-sm font-medium text-white transition-colors hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isFetching ? "Checking..." : "Track Order"}
          </button>
        </div>
      </form>

      {isFetching && submittedOrderId ? (
        <div className="mt-6 max-w-[720px] rounded-2xl border border-black/10 bg-white/50 p-5 text-sm text-[#22272A]">
          Checking order status for{" "}
          <span className="font-semibold">#{submittedOrderId}</span>...
        </div>
      ) : null}

      {isError && submittedOrderId && !isFetching ? (
        <div className="mt-6 max-w-[720px] rounded-2xl border border-red-200 bg-red-50 p-5 text-sm text-red-700">
          {getErrorMessage()}
        </div>
      ) : null}

      {orderStatus && !isError && !isFetching ? (
        <div className="mt-8 max-w-[900px] rounded-2xl border border-black/10 bg-white/50 p-5 sm:p-6">
          <div className="flex flex-col gap-4 border-b border-black/10 pb-5 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-[12px] font-semibold uppercase tracking-[0.08em] text-[#22272A]/60">
                Order ID
              </p>
              <h2 className="mt-1 break-all text-[20px] font-semibold text-[#22272A] sm:text-[24px]">
                #{orderStatus.order_id}
              </h2>
            </div>

            <div className="w-fit rounded-full bg-[#22272A] px-4 py-2 text-sm font-medium text-white">
              {orderStatus.status_display || orderStatus.status || "—"}
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <p className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[#22272A]/60">
                Order Status
              </p>
              <p className="mt-1 text-[15px] text-[#22272A]">
                {orderStatus.status_display || orderStatus.status || "—"}
              </p>
            </div>

            <div>
              <p className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[#22272A]/60">
                Payment Status
              </p>
              <p className="mt-1 text-[15px] text-[#22272A]">
                {orderStatus.payment_status || "—"}
              </p>
            </div>

            <div>
              <p className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[#22272A]/60">
                Total
              </p>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                {hasOrderDiscount ? (
                  <span className="text-[13px] text-[#8A8A8A] line-through">
                    ${formatPrice(originalOrderTotal)}
                  </span>
                ) : null}

                <span className="text-[15px] font-semibold text-[#22272A]">
                  ${formatPrice(orderTotal)}
                </span>
              </div>
              {hasOrderDiscount ? (
                <p className="mt-1 text-[12px] font-medium text-[#257A45]">
                  Saved ${formatPrice(orderSavings)}
                </p>
              ) : null}
            </div>

            <div>
              <p className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[#22272A]/60">
                Item Count
              </p>
              <p className="mt-1 text-[15px] text-[#22272A]">
                {orderStatus.item_count ?? "—"}
              </p>
            </div>

            <div>
              <p className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[#22272A]/60">
                Total Quantity
              </p>
              <p className="mt-1 text-[15px] text-[#22272A]">
                {orderStatus.total_quantity ?? "—"}
              </p>
            </div>

            <div>
              <p className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[#22272A]/60">
                Created At
              </p>
              <p className="mt-1 text-[15px] text-[#22272A]">
                {formatDate(orderStatus.created_at)}
              </p>
            </div>
            {orderStatus.paid_at ? (
              <div>
                <p className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[#22272A]/60">
                  Paid At
                </p>
                <p className="mt-1 text-[15px] text-[#22272A]">
                  {formatDate(orderStatus.paid_at)}
                </p>
              </div>
            ) : null}
          </div>

          {orderStatus.shipping_address ? (
            <div className="mt-6 rounded-xl bg-white/60 p-4">
              <p className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[#22272A]/60">
                Shipping Address
              </p>
              <p className="mt-1 text-[15px] leading-[150%] text-[#22272A]">
                {orderStatus.shipping_address}
              </p>
            </div>
          ) : null}

          {Array.isArray(orderStatus.items) && orderStatus.items.length > 0 ? (
            <div className="mt-8">
              <h3 className="text-[18px] font-semibold text-[#22272A]">
                Ordered Items
              </h3>

              <div className="mt-4 space-y-4">
                {orderEntries.map(
                  (entry) => {
                    if (entry.type === "bundle") {
                      return (
                        <TrackOrderBundleCard
                          key={entry.key}
                          bundle={entry}
                        />
                      );
                    }

                    return (
                      <TrackOrderItemCard
                        key={entry.key}
                        item={entry.item}
                      />
                    );
                  },
                )}
              </div>
            </div>
          ) : null}

          <div className="mt-8 flex justify-end border-t border-black/10 pt-6">
            <div className="w-full max-w-[340px] rounded-xl bg-white/70 p-4">
              <h3 className="text-[15px] font-semibold text-[#22272A]">
                Order Summary
              </h3>

              <div className="mt-4 space-y-3">
                <div className="flex items-center justify-between gap-4 text-[14px]">
                  <span className="text-[#22272A]/65">
                    Subtotal
                  </span>

                  <span className="font-medium text-[#22272A]">
                    ${formatPrice(originalOrderTotal)}
                  </span>
                </div>

                {hasOrderDiscount ? (
                  <div className="flex items-center justify-between gap-4 text-[14px]">
                    <span className="text-[#257A45]">
                      Discount
                    </span>

                    <span className="font-medium text-[#257A45]">
                      -${formatPrice(orderSavings)}
                    </span>
                  </div>
                ) : null}

                <div className="flex items-center justify-between gap-4 border-t border-black/10 pt-3">
                  <span className="font-semibold text-[#22272A]">
                    Total
                  </span>

                  <span className="text-[18px] font-semibold text-[#22272A]">
                    ${formatPrice(orderTotal)}
                  </span>
                </div>
              </div>

              {hasOrderDiscount ? (
                <div className="mt-4 rounded-lg border border-[#CFE8D7] bg-[#F1FAF4] px-3 py-2.5 text-center text-[12px] font-medium text-[#257A45]">
                  You saved ${formatPrice(orderSavings)} on this
                  order
                </div>
              ) : null}
            </div>
          </div>

          {orderStatus.cancel_reason ? (
            <div className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              Cancel Reason: {orderStatus.cancel_reason}
            </div>
          ) : null}
        </div>
      ) : null}
    </StaticContentPage>
  );
};

export default TrackYourOrder;